<template>
  <div>
    <!-- Loading State -->
    <div v-if="videoDevicesLoading" class="mt-4">
      <v-row>
        <v-col
          v-for="n in 9"
          :key="`video-skeleton-${n}`"
          :cols="gridConfig.mobile"
          :sm="gridConfig.tablet"
          :lg="gridConfig.desktop"
          class="px-1 py-1"
        >
          <v-skeleton-loader type="image" />
        </v-col>
      </v-row>
    </div>

    <!-- No Results State -->
    <div
      v-else-if="videoDevices.length === 0"
      class="text-center grey--text mt-4"
      height="230px"
    >
      No video cameras available
    </div>

    <!-- Results State -->
    <div v-else class="mt-4">
      <!-- Grid Info Display (for debugging/info) -->
      <div v-if="showGridInfo" class="mb-2 text-center">
        <v-chip small color="info" text-color="white">
          {{ videoDevices.length }} cameras - {{ gridConfig.columns }} columns -
          {{ cardHeight }}px height
        </v-chip>
      </div>

      <v-row class="px-3 pb-3">
        <v-col
          v-for="(device, index) in videoDevices"
          :key="index"
          :cols="gridConfig.mobile"
          :sm="gridConfig.tablet"
          :lg="gridConfig.desktop"
          class="px-1 py-1"
        >
          <v-card
            elevation="2"
            class="video-card rounded-lg"
            @click="openVideoDialog(device)"
            :style="{ height: cardHeight + 'px' }"
          >
            <div class="video-header-overlay">
              <div class="header-left">
                <div class="camera-name-container">
                  <v-icon
                    left
                    x-small
                    :color="
                      deviceAuthStatus[device.externalId]?.online
                        ? 'success'
                        : 'error'
                    "
                    >mdi-circle</v-icon
                  >
                  <span class="camera-name">{{ device.zoneName }}</span>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="white"
                        class="ml-1 info-icon"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="showDeviceInfo(device)"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div class="camera-tooltip">
                      <div>
                        <strong>{{ device.zoneName }}</strong>
                      </div>
                      <div>Device ID: {{ device.shortId }}</div>
                      <div>External ID: {{ device.externalId }}</div>
                      <div>
                        Status:
                        {{
                          deviceAuthStatus[device.externalId]?.online
                            ? "Online"
                            : "Offline"
                        }}
                      </div>
                    </div>
                  </v-tooltip>
                </div>
              </div>
              <div class="header-right">
                <!-- Fullscreen Icon -->
                <v-btn
                  icon
                  x-small
                  class="ml-1 fullscreen-btn"
                  @click.stop="openVideoDialog(device)"
                  title="Open Fullscreen"
                >
                  <v-icon small color="white">mdi-fullscreen</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Video Container -->
            <div
              class="video-container position-relative rounded-lg"
              :style="{
                height: videoContainerHeight + 'px',
                backgroundColor: '#000',
              }"
            >
              <!-- Loading indicator -->
              <v-progress-circular
                v-if="!deviceAuthStatus[device.externalId]?.ready"
                indeterminate
                color="primary"
                class="video-loader"
              />

              <!-- Live Video Preview using EEN SDK -->
              <div
                v-if="
                  deviceAuthStatus[device.externalId]?.ready &&
                  !deviceAuthStatus[device.externalId]?.error
                "
                class="live-preview-container"
              >
                <!-- EEN Live Video Container -->
                <div
                  :id="`een_preview_${device.externalId}`"
                  class="een-video-preview"
                  @click.stop="openVideoDialog(device)"
                ></div>

                <!-- Refresh overlay (appears on hover) -->
                <div class="preview-controls">
                  <v-btn
                    icon
                    small
                    color="white"
                    @click.stop="refreshEENPreview(device.externalId)"
                    title="Refresh Preview"
                    class="refresh-btn"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </div>

                <!-- Play overlay (center) -->
                <div class="play-overlay" @click.stop="openVideoDialog(device)">
                  <v-btn icon color="white" class="play-button">
                    <v-icon large>mdi-play-circle</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Error State -->
              <div
                v-if="deviceAuthStatus[device.externalId]?.error"
                class="error-state"
              >
                <v-icon large color="error">mdi-video-off</v-icon>
                <div class="error-text mt-2">
                  <div class="subtitle-2">
                    {{
                      deviceAuthStatus[device.externalId]?.online
                        ? "Preview Unavailable"
                        : "Camera Offline"
                    }}
                  </div>
                  <div class="caption">{{ device.zoneName }}</div>
                </div>
                <v-btn
                  small
                  color="primary"
                  class="mt-2"
                  @click.stop="retryPreview(device)"
                >
                  Retry
                </v-btn>
              </div>

              <div class="video-time-overlay">
                <div class="camera-time">{{ getCurrentTime() }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Enhanced Fullscreen Video Dialog -->
    <v-dialog v-model="videoDialog" fullscreen hide-overlay>
      <v-card>
        <v-card-title class="headline">
          <v-toolbar-title>
            <v-icon
              left
              small
              class="mb-1"
              :color="currentViewMode === 'live' ? 'success' : 'warning'"
            >
              {{ currentViewMode === "live" ? "mdi-circle" : "mdi-history" }}
            </v-icon>
            {{ currentVideoDevice?.zoneName }} -
            {{ currentViewMode === "live" ? "Live Video" : "Playback" }}
          </v-toolbar-title>

          <v-spacer />

          <!-- View Mode Toggle -->
          <div class="view-mode-toggle mr-4">
            <v-btn-toggle v-model="currentViewMode" dense mandatory>
              <v-btn value="live" small>
                <v-icon left color="success" small>mdi-circle</v-icon>
                Live
              </v-btn>
              <v-btn value="playback" small>
                <v-icon left color="warning" small>mdi-history</v-icon>
                Playback
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Camera Navigation -->
          <div class="camera-nav-buttons mr-4">
            <v-btn
              icon
              :disabled="!canSwitchToPrevious"
              title="Previous Camera"
              @click="switchToPreviousCamera"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-chip color="info" text-color="white" small class="mx-2">
              {{ currentCameraIndex + 1 }} / {{ videoDevices.length }}
            </v-chip>
            <v-btn
              icon
              :disabled="!canSwitchToNext"
              title="Next Camera"
              @click="switchToNextCamera"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <v-btn icon @click="closeVideoDialog">
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0 fullscreen-container">
          <div class="video-player-wrapper">
            <!-- Live Video View -->
            <div
              v-show="currentViewMode === 'live'"
              class="video-view-container"
            >
              <!-- EEN SDK Video Player -->
              <div
                v-if="currentVideoDevice"
                :id="`een_fullscreen_${currentVideoDevice.externalId}`"
                :key="`fullscreen_${currentVideoDevice.externalId}`"
                class="een-player-container"
              ></div>

              <!-- Live Loading state -->
              <div v-if="fullscreenLoading" class="fullscreen-loading">
                <v-progress-circular indeterminate color="primary" size="64" />
                <div class="mt-4">
                  Loading Live Video - {{ currentVideoDevice?.zoneName }}...
                </div>
              </div>

              <!-- Live Error state -->
              <div v-if="playerError" class="fullscreen-error">
                <v-icon large color="warning">mdi-alert-circle</v-icon>
                <div class="mt-2">{{ playerError }}</div>
                <v-btn
                  color="primary"
                  class="mt-2"
                  @click="retryFullscreenPlayer"
                >
                  Retry Live
                </v-btn>
              </div>
            </div>

            <!-- Playback Video View -->
            <div
              v-show="currentViewMode === 'playback'"
              class="video-view-container"
            >
              <div v-if="authStatus.value">
                <iframe
                  v-if="showIframe"
                  id="sd_vms_iframe"
                  height="600px"
                  width="100%"
                  title="History Browser"
                  style="background-color: #000; min-height: calc(100vh - 64px)"
                  allow="autoplay"
                  :src="iframeSrc"
                  allowfullscreen
                  @load="setupIframeListener"
                ></iframe>
              </div>
              <div v-else class="text-center py-4">
                <v-progress-circular indeterminate color="primary" />
                <div class="mt-2">Loading authentication...</div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { EEN_AUTH_STATUS } from "../fixtures/graphql";
import useFacility from "../authentication/useFacility";
import { organizationSmartZones } from "../../graphql/organizations";
import LivePlayer from "@een/live-video-web-sdk";

export default {
  name: "VideoGridLive",
  setup() {
    const { currentFacilityId } = useFacility();
    return {
      currentFacilityId,
    };
  },
  data() {
    return {
      videoDevicesLoading: true,
      videoDevices: [],
      videoDialog: false,
      infoDialog: false,
      currentVideoDevice: null,
      selectedDevice: null,
      authStatus: { value: false, accessToken: null },
      deviceAuthStatus: {},
      previewIntervals: {},
      playerError: null,
      currentTime: new Date().toLocaleTimeString(),
      refreshTimeouts: {},
      fullscreenLoading: false,
      currentCameraIndex: 0,
      eenPreviewPlayers: {},
      eenFullscreenPlayer: null,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      showGridInfo: false,
      currentViewMode: "live",
      showIframe: false,
    };
  },

  computed: {
    canSwitchToPrevious() {
      return this.currentCameraIndex > 0;
    },
    canSwitchToNext() {
      return this.currentCameraIndex < this.videoDevices.length - 1;
    },

    gridConfig() {
      const count = this.videoDevices.length;
      const isDesktop = this.windowWidth >= 1264;
      const isTablet = this.windowWidth >= 600 && this.windowWidth < 1264;
      const isMobile = this.windowWidth < 600;

      const availableHeight = this.windowHeight - 120;

      let config = {
        columns: 3,
        desktop: 4,
        tablet: 6,
        mobile: 12,
      };

      if (isDesktop) {
        if (count <= 3) {
          config = { columns: 3, desktop: 4, tablet: 6, mobile: 12 };
        } else if (count <= 6) {
          config = { columns: 3, desktop: 4, tablet: 6, mobile: 12 };
        } else if (count <= 8) {
          config = { columns: 4, desktop: 3, tablet: 6, mobile: 12 };
        } else if (count <= 12) {
          config = { columns: 4, desktop: 3, tablet: 6, mobile: 12 };
        } else if (count <= 15) {
          config = { columns: 5, desktop: 2.4, tablet: 4, mobile: 12 };
        } else if (count <= 18) {
          config = { columns: 6, desktop: 2, tablet: 4, mobile: 12 };
        } else {
          config = { columns: 6, desktop: 2, tablet: 4, mobile: 12 };
        }
      } else if (isTablet) {
        if (count <= 6) {
          config = { columns: 2, desktop: 4, tablet: 6, mobile: 12 };
        } else if (count <= 9) {
          config = { columns: 3, desktop: 4, tablet: 4, mobile: 12 };
        } else {
          config = { columns: 3, desktop: 4, tablet: 4, mobile: 12 };
        }
      } else {
        config = { columns: 1, desktop: 4, tablet: 6, mobile: 12 };
      }

      return config;
    },

    cardHeight() {
      const count = this.videoDevices.length;
      const columns = this.gridConfig.columns;
      const availableHeight = this.windowHeight - 150;

      const optimalRows = Math.ceil(count / columns);
      const maxRowsOnScreen = Math.floor(availableHeight / 200);

      let targetRows = Math.min(optimalRows, Math.max(2, maxRowsOnScreen));

      let height = Math.floor(availableHeight / targetRows) - 32;

      if (height < 180) height = 180;
      if (height > 300) height = 300;

      if (this.windowWidth < 600) {
        height = Math.min(300, height);
      } else if (this.windowWidth < 1264) {
        height = Math.min(280, height);
      }

      return height;
    },

    videoContainerHeight() {
      return this.cardHeight;
    },

    iframeSrc() {
      return this.currentVideoDevice
        ? `https://iframe.eagleeyenetworks.com/#/historylive/${this.currentVideoDevice.externalId}`
        : "";
    },
  },

  watch: {
    currentViewMode: {
      async handler(newMode, oldMode) {
        if (newMode !== oldMode) {
          if (newMode === "live" && this.currentVideoDevice) {
            this.cancel();
            this.$nextTick(async () => {
              await this.initializeFullscreenPlayer();
            });
          } else if (newMode === "playback") {
            await this.cleanupFullscreenPlayer();
            this.fetchAuthenticationStatus();
          }
        }
      },
    },

    currentVideoDevice: {
      handler(newDevice, oldDevice) {
        if (newDevice && newDevice.externalId !== oldDevice?.externalId) {
          this.cancel();

          if (this.currentViewMode === "live") {
            this.initializeFullscreenPlayer();
          } else if (this.currentViewMode === "playback") {
            this.fetchAuthenticationStatus();
          }
        }
      },
    },
  },
  mounted() {
    this.fetchVideoDevices();

    setInterval(this.updateCurrentTime, 1000);

    window.addEventListener("resize", this.handleResize);
  },

  async beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);

    for (const deviceId in this.eenPreviewPlayers) {
      try {
        await this.eenPreviewPlayers[deviceId].stop();
      } catch (error) {
        console.error(`Error stopping preview for device ${deviceId}:`, error);
      }
    }

    await this.cleanupFullscreenPlayer();
    this.cancel();

    Object.values(this.previewIntervals).forEach((interval) => {
      clearInterval(interval);
    });

    Object.values(this.refreshTimeouts).forEach((timeout) => {
      clearTimeout(timeout);
    });
  },
  methods: {
    async fetchVideoDevices() {
      try {
        this.videoDevicesLoading = true;

        const { data } = await this.$apollo.query({
          query: organizationSmartZones,
          variables: {
            id: this.currentFacilityId,
          },
          fetchPolicy: "network-only",
        });

        let allVideoDevices = [];
        const smartZones = data?.smartZones || [];

        smartZones.forEach((zone) => {
          if (zone.devices?.length) {
            const zoneVideoDevices = zone.devices.filter(
              (d) => d.type === "een" && d.subtype === "video_camera"
            );
            const devicesWithZoneName = zoneVideoDevices.map((device) => ({
              ...device,
              zoneName: zone.name || "Unknown Zone",
            }));
            allVideoDevices.push(...devicesWithZoneName);
          }
        });

        const uniqueDeviceMap = {};
        allVideoDevices.forEach((d) => (uniqueDeviceMap[d.externalId] = d));
        this.videoDevices = Object.values(uniqueDeviceMap);

        this.initializeDeviceAuth();
      } catch (error) {
        console.error("Error fetching video devices:", error);
      } finally {
        this.videoDevicesLoading = false;
      }
    },

    handleResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
    },

    async initializeDeviceAuth() {
      this.videoDevices.forEach((device) => {
        this.$set(this.deviceAuthStatus, device.externalId, {
          ready: false,
          accessToken: null,
          error: null,
          online: true,
        });
      });

      try {
        const response = await this.$apollo.query({
          query: EEN_AUTH_STATUS,
          variables: { organization_id: this.currentFacilityId },
        });
        const authData = response.data.eenAuthStatus;

        if (authData.accessToken) {
          this.authStatus = { value: true, accessToken: authData.accessToken };

          this.videoDevices.forEach((device) => {
            this.$set(this.deviceAuthStatus[device.externalId], "ready", true);
            this.$set(
              this.deviceAuthStatus[device.externalId],
              "accessToken",
              authData.accessToken
            );
          });

          this.$nextTick(() => {
            this.startAllEENPreviews();
          });
        } else {
          this.videoDevices.forEach((device) => {
            this.$set(
              this.deviceAuthStatus[device.externalId],
              "error",
              "Authentication required"
            );
            this.$set(
              this.deviceAuthStatus[device.externalId],
              "online",
              false
            );
          });
        }
      } catch (error) {
        console.error("Authentication error:", error);
        this.videoDevices.forEach((device) => {
          this.$set(
            this.deviceAuthStatus[device.externalId],
            "error",
            "Authentication failed"
          );
          this.$set(this.deviceAuthStatus[device.externalId], "online", false);
        });
      }
    },

    async startAllEENPreviews() {
      for (const device of this.videoDevices) {
        await this.createEENPreview(device.externalId);
      }
    },

    async createEENPreview(deviceId) {
      if (this.eenPreviewPlayers[deviceId]) return;

      try {
        await this.$nextTick();

        const videoElement = document.getElementById(`een_preview_${deviceId}`);
        if (!videoElement) {
          console.warn(`Video element not found for device ${deviceId}`);
          return;
        }

        let video = videoElement.querySelector("video");
        if (!video) {
          video = document.createElement("video");
          video.autoplay = true;
          video.muted = true;
          video.playsInline = true;
          video.controls = false;
          video.style.width = "100%";
          video.style.height = "100%";
          video.style.objectFit = "cover";
          videoElement.appendChild(video);
        }

        const config = {
          videoElement: video,
          cameraId: deviceId,
          baseUrl: "https://api.c000.eagleeyenetworks.com",
          jwt: this.authStatus.accessToken,
        };

        const player = new LivePlayer();
        this.eenPreviewPlayers[deviceId] = player;

        video.addEventListener("loadstart", () => {
          console.log(`Preview loading for device ${deviceId}`);
        });

        video.addEventListener("canplay", () => {
          this.$set(this.deviceAuthStatus[deviceId], "error", null);
          this.$set(this.deviceAuthStatus[deviceId], "online", true);
          console.log(`Preview started for device ${deviceId}`);
        });

        video.addEventListener("error", (error) => {
          console.error(`Preview error for device ${deviceId}:`, error);
          this.$set(
            this.deviceAuthStatus[deviceId],
            "error",
            "Preview unavailable"
          );
          this.$set(this.deviceAuthStatus[deviceId], "online", false);
        });

        try {
          await player.start(config);
        } catch (sdkError) {
          console.error(`SDK Error for device ${deviceId}:`, sdkError);

          if (config.baseUrl.includes("api.c000")) {
            console.log(`Retrying with login URL for device ${deviceId}`);
            config.baseUrl = "https://login.eagleeyenetworks.com";
            await player.start(config);
          } else {
            throw sdkError;
          }
        }
      } catch (error) {
        console.error(
          `Failed to create preview for device ${deviceId}:`,
          error
        );
        this.$set(
          this.deviceAuthStatus[deviceId],
          "error",
          "Failed to load preview"
        );
        this.$set(this.deviceAuthStatus[deviceId], "online", false);

        this.fallbackToImagePreview(deviceId);
      }
    },

    fallbackToImagePreview(deviceId) {
      const videoElement = document.getElementById(`een_preview_${deviceId}`);
      if (!videoElement) return;

      videoElement.innerHTML = "";

      const img = document.createElement("img");
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "cover";
      img.alt = "Camera Preview";

      img.src = this.getLivePreviewUrl(deviceId);

      img.onload = () => {
        this.$set(this.deviceAuthStatus[deviceId], "error", null);
        this.$set(this.deviceAuthStatus[deviceId], "online", true);
      };

      img.onerror = () => {
        this.$set(
          this.deviceAuthStatus[deviceId],
          "error",
          "Preview unavailable"
        );
        this.$set(this.deviceAuthStatus[deviceId], "online", false);
      };

      videoElement.appendChild(img);

      if (this.previewIntervals[deviceId]) {
        clearInterval(this.previewIntervals[deviceId]);
      }

      this.previewIntervals[deviceId] = setInterval(() => {
        img.src = this.getLivePreviewUrl(deviceId);
      }, 5000);
    },

    async refreshEENPreview(deviceId) {
      try {
        if (this.eenPreviewPlayers[deviceId]) {
          await this.eenPreviewPlayers[deviceId].stop();
          delete this.eenPreviewPlayers[deviceId];
        }

        const videoElement = document.getElementById(`een_preview_${deviceId}`);
        if (videoElement) {
          videoElement.innerHTML = "";
        }

        setTimeout(async () => {
          await this.createEENPreview(deviceId);
        }, 500);
      } catch (error) {
        console.error(
          `Failed to refresh preview for device ${deviceId}:`,
          error
        );
      }
    },

    getLivePreviewUrl(deviceId) {
      const token =
        this.deviceAuthStatus[deviceId]?.accessToken ||
        this.authStatus.accessToken;
      if (!token) return "";

      const timestamp = Date.now();
      return `https://login.eagleeyenetworks.com/asset/asset/image.jpeg?id=${deviceId}&A=${token}&timestamp=${timestamp}&size=640x480&quality=med&_cb=${Math.random()}`;
    },

    async refreshPreview(deviceId, manual = false) {
      if (manual) {
        this.$set(this.deviceAuthStatus[deviceId], "error", null);
      }

      await this.refreshEENPreview(deviceId);
    },

    retryPreview(device) {
      this.$set(this.deviceAuthStatus[device.externalId], "error", null);
      this.$set(this.deviceAuthStatus[device.externalId], "online", true);
      this.refreshPreview(device.externalId, true);
    },

    onPreviewLoad(deviceId) {
      this.$set(this.deviceAuthStatus[deviceId], "error", null);
      this.$set(this.deviceAuthStatus[deviceId], "online", true);
    },

    onPreviewError(deviceId) {
      console.error(`Preview failed for device ${deviceId}`);
      this.$set(
        this.deviceAuthStatus[deviceId],
        "error",
        "Preview unavailable"
      );
      this.$set(this.deviceAuthStatus[deviceId], "online", false);
    },

    showDeviceInfo(device) {
      this.selectedDevice = device;
    },

    async openVideoDialog(device) {
      await this.cleanupFullscreenPlayer();
      this.cancel();

      this.currentVideoDevice = device;
      this.currentCameraIndex = this.videoDevices.findIndex(
        (d) => d.externalId === device.externalId
      );
      this.playerError = null;
      this.fullscreenLoading = true;
      this.currentViewMode = "live";

      this.videoDialog = true;

      this.$nextTick(async () => {
        await this.initializeFullscreenPlayer();
      });
    },

    async closeVideoDialog() {
      await this.cleanupFullscreenPlayer();
      this.cancel();
      this.videoDialog = false;
      this.currentVideoDevice = null;
      this.playerError = null;
      this.fullscreenLoading = false;
      this.currentCameraIndex = 0;
      this.currentViewMode = "live";
    },

    async switchToPreviousCamera() {
      if (this.canSwitchToPrevious) {
        const newIndex = this.currentCameraIndex - 1;
        await this.switchToCamera(newIndex);
      }
    },

    async switchToNextCamera() {
      if (this.canSwitchToNext) {
        const newIndex = this.currentCameraIndex + 1;
        await this.switchToCamera(newIndex);
      }
    },

    async switchToCamera(index) {
      if (index >= 0 && index < this.videoDevices.length) {
        await this.cleanupFullscreenPlayer();
        this.cancel();

        this.currentCameraIndex = index;
        this.currentVideoDevice = this.videoDevices[index];
        this.fullscreenLoading = true;
        this.playerError = null;

        this.$forceUpdate();

        this.$nextTick(async () => {
          if (this.currentViewMode === "live") {
            await this.initializeFullscreenPlayer();
          } else if (this.currentViewMode === "playback") {
            this.fetchAuthenticationStatus();
          }
        });
      }
    },

    async initializeFullscreenPlayer() {
      if (!this.currentVideoDevice || !this.authStatus.accessToken) {
        this.fullscreenLoading = false;
        return;
      }

      try {
        this.fullscreenLoading = true;

        await new Promise((resolve) => setTimeout(resolve, 100));

        const container = document.getElementById(
          `een_fullscreen_${this.currentVideoDevice.externalId}`
        );
        if (!container) {
          console.error("Fullscreen container not found");
          this.fullscreenLoading = false;
          return;
        }

        container.innerHTML = "";

        const video = document.createElement("video");
        video.autoplay = true;
        video.muted = false;
        video.playsInline = true;
        video.controls = true;
        video.style.width = "100%";
        video.style.height = "100%";
        video.style.objectFit = "contain";
        video.style.backgroundColor = "#000";
        container.appendChild(video);

        const config = {
          videoElement: video,
          cameraId: this.currentVideoDevice.externalId,
          baseUrl: "https://api.c000.eagleeyenetworks.com",
          jwt: this.authStatus.accessToken,
        };

        this.eenFullscreenPlayer = new LivePlayer();

        video.addEventListener("canplay", () => {
          this.playerError = null;
          this.fullscreenLoading = false;
          console.log(
            `Fullscreen video started for ${this.currentVideoDevice.zoneName}`
          );
        });

        video.addEventListener("error", (error) => {
          console.error("Fullscreen video error:", error);
          this.playerError = "Failed to load video stream";
          this.fullscreenLoading = false;
        });

        video.addEventListener("loadstart", () => {
          console.log(
            `Loading fullscreen video for ${this.currentVideoDevice.zoneName}`
          );
        });

        try {
          await this.eenFullscreenPlayer.start(config);
        } catch (sdkError) {
          console.error("Fullscreen SDK Error:", sdkError);

          if (config.baseUrl.includes("api.c000")) {
            console.log("Retrying fullscreen with login URL");
            config.baseUrl = "https://login.eagleeyenetworks.com";
            await this.eenFullscreenPlayer.start(config);
          } else {
            throw sdkError;
          }
        }
      } catch (error) {
        console.error("Failed to initialize fullscreen player:", error);
        this.playerError = "Failed to initialize video player";
        this.fullscreenLoading = false;
      }
    },

    async cleanupFullscreenPlayer() {
      if (this.eenFullscreenPlayer) {
        try {
          await this.eenFullscreenPlayer.stop();
          this.eenFullscreenPlayer = null;
          console.log("Fullscreen player cleaned up");
        } catch (error) {
          console.error("Error stopping EEN fullscreen player:", error);
        }
      }

      if (this.currentVideoDevice) {
        const container = document.getElementById(
          `een_fullscreen_${this.currentVideoDevice.externalId}`
        );
        if (container) {
          container.innerHTML = "";
        }
      }
    },

    async retryFullscreenPlayer() {
      this.playerError = null;
      await this.cleanupFullscreenPlayer();
      this.$nextTick(async () => {
        await this.initializeFullscreenPlayer();
      });
    },

    cancel() {
      window.removeEventListener("message", this.handleIframeMessage);
      this.showIframe = false;
    },

    async fetchAuthenticationStatus() {
      try {
        const response = await this.$apollo.query({
          query: EEN_AUTH_STATUS,
          variables: { organization_id: this.currentFacilityId },
        });
        const authStatusData = response.data.eenAuthStatus;

        if (authStatusData.accessToken) {
          this.authStatus.value = true;
          this.authStatus.accessToken = authStatusData.accessToken;
          this.showIframe = true;

          this.$nextTick(() => {
            setTimeout(() => {
              this.setupIframeListener();
            }, 500);
          });
        }
      } catch (error) {
        console.error(error);
      }
    },

    setupIframeListener() {
      const iframe = document.getElementById("sd_vms_iframe");
      if (iframe) {
        this.handleIframeLoad();
      } else {
        setTimeout(() => {
          this.setupIframeListener();
        }, 300);
      }
    },

    handleIframeLoad() {
      const iframe = document.getElementById("sd_vms_iframe");
      const iframeWindow = iframe?.contentWindow;
      if (iframeWindow) {
        window.removeEventListener("message", this.handleIframeMessage);
        window.addEventListener("message", this.handleIframeMessage);

        this.sendTokenToIframe();
      } else {
        console.error("Iframe contentWindow is null");
      }
    },

    sendTokenToIframe() {
      const iframe = document.getElementById("sd_vms_iframe");
      if (iframe && iframe.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authStatus.accessToken,
            },
            "https://iframe.eagleeyenetworks.com/"
          );
        }, 500);

        setTimeout(() => {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authStatus.accessToken,
            },
            "https://iframe.eagleeyenetworks.com/"
          );
        }, 2000);
      }
    },

    handleIframeMessage(event) {
      if (
        event.data === "een-iframe-loaded" ||
        event.data === "een-iframe-token-expired"
      ) {
        const iframe = document.getElementById("sd_vms_iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "een-token",
              token: this.authStatus.accessToken,
            },
            "https://iframe.eagleeyenetworks.com/"
          );
        } else {
          console.error("Iframe or iframe contentWindow is null");
        }
      }
    },

    getCurrentTime() {
      return this.currentTime;
    },

    updateCurrentTime() {
      this.currentTime = new Date().toLocaleTimeString();
    },
  },
};
</script>

<style scoped>
/* EEN SDK specific styles */
.een-video-preview {
  width: 100%;
  height: 100%;
  background-color: #000;
  position: relative;
}

.een-player-container {
  width: 100%;
  height: 100%;
  background-color: #000;
}

.video-card {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.video-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.video-container {
  position: relative;
  overflow: hidden;
}

.video-loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.video-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 8;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, transparent 100%);
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.video-container {
  position: relative;
  overflow: hidden;
  border-radius: 12px; /* Match card border radius */
}

.rounded-lg {
  border-radius: 12px !important;
}

/* New time overlay at bottom left */
.video-time-overlay {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 10;
  background: #0000;
  padding: 0px 4px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

.video-time-overlay .camera-time {
  font-size: 11px;
  font-weight: bold;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.header-left {
  color: white;
}

.camera-time {
  font-size: 11px;
  font-weight: bold;
  margin-bottom: 2px;
}

.camera-name-container {
  display: flex;
  align-items: center;
}

.camera-name {
  font-size: 13px;
  font-weight: 500;
}

.info-icon {
  cursor: pointer;
  transition: opacity 0.2s;
}

.info-icon:hover {
  opacity: 0.7;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.fullscreen-btn {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.live-preview-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.preview-controls {
  position: absolute;
  bottom: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 15;
}

.video-card:hover .preview-controls {
  opacity: 1;
}

.refresh-btn {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 12;
}

.video-card:hover .play-overlay {
  opacity: 0.9;
}

.play-button {
  background-color: rgba(0, 0, 0, 0.6) !important;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: white;
  text-align: center;
  background-color: #1a1a1a;
}

.error-text {
  margin: 8px 0;
}

.fullscreen-container {
  height: calc(100vh - 64px);
  background-color: #000;
}

.video-player-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.fullscreen-loading,
.fullscreen-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
}

.camera-nav-buttons {
  display: flex;
  align-items: center;
}

/* Tooltip styling */
.camera-tooltip {
  text-align: left;
  font-size: 12px;
  line-height: 1.4;
}

.camera-tooltip div {
  margin-bottom: 2px;
}

.camera-tooltip div:last-child {
  margin-bottom: 0;
}

/* Responsive grid adjustments */
@media (max-width: 599px) {
  .camera-name {
    font-size: 12px;
  }

  .camera-time {
    font-size: 10px;
  }
}
</style>
